import { Flex, Text } from '@chakra-ui/react'
import { Download } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import {
  usePartnerDesktopAppDownloadUrl,
  usePartnerDesktopAppName,
} from '~/hooks/useCurrentPartner'
import {
  type DesktopAppLaunchConfirm,
  DesktopAppLaunchStep,
} from '~/hooks/useDesktopAppLaunchConfirm'
import { Size } from '~/types/ThemeTypes'
import { openInNewTab } from '~/utils/functions'

import { Modal } from '@mangovoice/mango-ui'

interface DesktopAppLaunchConfirmModalProps {
  launchConfirm: DesktopAppLaunchConfirm
  /** Called when the user answers "Yes, it opened". */
  onConfirmed: () => void
  /** Optional muted line under the "Did it open?" question. */
  confirmHint?: string
  /** Defaults to the Settings opt-in title. */
  title?: string
}

/**
 * Asks whether a desktop-app launch worked. Ignores outside clicks and focus
 * changes, since the browser regains focus when the user switches back from
 * the app; Escape and the close button still dismiss it.
 */
export const DesktopAppLaunchConfirmModal: React.FC<
  DesktopAppLaunchConfirmModalProps
> = ({ launchConfirm, onConfirmed, confirmHint, title }) => {
  const { t } = useTranslation()
  const { step, retry, markNotOpened, close } = launchConfirm
  const appName = usePartnerDesktopAppName()
  const downloadUrl = usePartnerDesktopAppDownloadUrl()

  // Keeps the last step's content rendered while the modal animates closed.
  const [shownStep, setShownStep] = useState(
    step ?? DesktopAppLaunchStep.Confirm,
  )
  if (step !== null && step !== shownStep) setShownStep(step)

  const isConfirmStep = shownStep === DesktopAppLaunchStep.Confirm

  const footer = isConfirmStep ? (
    <Flex gap={3} justifyContent="flex-end" width="100%" pt={4}>
      <MangoButton
        size={Size.md}
        variant={MangoButtonVariant.Default_200}
        onClick={markNotOpened}
      >
        {t('profileSettings.desktopApp.testLaunch.no', "No, it didn't")}
      </MangoButton>
      <MangoButton
        size={Size.md}
        variant={MangoButtonVariant.Brand}
        onClick={onConfirmed}
      >
        {t('profileSettings.desktopApp.testLaunch.yes', 'Yes, it opened')}
      </MangoButton>
    </Flex>
  ) : (
    <Flex gap={3} justifyContent="flex-end" width="100%" pt={4}>
      <MangoButton
        size={Size.md}
        variant={MangoButtonVariant.Default_200}
        onClick={close}
      >
        {t('profileSettings.desktopApp.testLaunch.close', 'Close')}
      </MangoButton>
      <MangoButton
        size={Size.md}
        variant={MangoButtonVariant.Default_200}
        onClick={retry}
      >
        {t('profileSettings.desktopApp.testLaunch.tryAgain', 'Try again')}
      </MangoButton>
      <MangoButton
        size={Size.md}
        variant={MangoButtonVariant.Brand}
        leftIcon={Download}
        onClick={() => openInNewTab(downloadUrl)}
      >
        {t('profileSettings.downloadApp', 'Download App')}
      </MangoButton>
    </Flex>
  )

  return (
    <Modal
      open={step !== null}
      onOpenChange={close}
      title={
        title ??
        t(
          'profileSettings.desktopApp.testLaunch.title',
          'Open links in the desktop app',
        )
      }
      footer={footer}
      maxWidth="480px"
      closeOnInteractOutside={false}
    >
      {isConfirmStep ? (
        <Flex flexDirection="column" gap={2}>
          <Text color="fg.default">
            {t('profileSettings.desktopApp.testLaunch.question', {
              appName,
              defaultValue:
                'We just tried to open the {{appName}} desktop app. Did it open?',
            })}
          </Text>
          {confirmHint ? (
            <Text fontSize="sm" color="fg.muted">
              {confirmHint}
            </Text>
          ) : null}
        </Flex>
      ) : (
        <Text color="fg.default">
          {t('profileSettings.desktopApp.testLaunch.notOpened', {
            appName,
            defaultValue:
              'Links will keep opening in this browser. Install the {{appName}} desktop app, then try again.',
          })}
        </Text>
      )}
    </Modal>
  )
}
